import type { MaxPopupMenu } from '@maxgraph/core';
import type { Store } from '@/graph-store';
import type { ToastCallbacks } from './ContextMenuService';
import { getGroupForOperation } from '@/graph/domain/groupValidation';
import { analyzeGroupSubgraph } from '@/graph/domain/groupTopology';
import { getEditBlockedMessage } from '@/utils/editBlockedMessages';

/**
 * Convert step ID to user-friendly display name
 */
function formatStepForDisplay(stepId: string, store: Store): string {
  const step = store.graphData.operationNodes[stepId];
  if (!step) return `Step (unknown)`;
  return step.displayName || `Step ${stepId.slice(0, 8)}`;
}

export function buildOperationMenu(
  menu: MaxPopupMenu,
  operationId: string,
  store: Store,
  toast?: ToastCallbacks,
): void {
  const isLocked = store.ui.isProtocolLocked || store.ui.isLifecycleLocked || store.ui.isArchived || store.ui.isArchivalInProgress || store.ui.runsExist || store.ui.canEditContent === false;
  const blockedState = {
    isProtocolLocked: store.ui.isProtocolLocked,
    isLifecycleLocked: store.ui.isLifecycleLocked,
    isArchived: store.ui.isArchived,
    isArchivalInProgress: store.ui.isArchivalInProgress,
    runsExist: store.ui.runsExist,
    canEditContent: store.ui.canEditContent,
  };

  menu.addItem('Edit Step', '/icon/modify.svg', () => {
    if (isLocked) {
      toast?.showWarning(getEditBlockedMessage(blockedState));
      return;
    }
    store.openModal({
      kind: 'edit-step',
      payload: { operationId },
    });
  });

  menu.addItem('Delete Step', '/icon/delete.svg', () => {
    if (isLocked) {
      toast?.showWarning(getEditBlockedMessage(blockedState));
      return;
    }
    store.openModal({
      kind: 'confirm-delete',
      payload: { kind: 'operation', operationId },
    });
  });

  // Cycle creation if op is in a group
  const group = getGroupForOperation(operationId, store.graphData.groups);
  if (group) {
    menu.addItem('Add Cycle Here', null, () => {
      if (isLocked) {
        toast?.showWarning(getEditBlockedMessage(blockedState));
        return;
      }
      store.openModal({
        kind: 'add-cycle',
        payload: { groupId: group.id, anchorOpId: operationId },
      });
    });
  }

  // Group from selection (multi-select)
  const selected = store.selection.cellIds;
  if (selected.includes(operationId) && selected.length > 1) {
    const opIds = selected.filter((id) => store.graphData.operationNodes[id]);
    if (opIds.length > 1) {
      // Pre-validate the selection
      const validation = analyzeGroupSubgraph(opIds, store.graphData);
      const isValid = validation.ok && !opIds.some((id) =>
        Object.values(store.graphData.groups).some((g) => g.stepIds.includes(id)),
      );

      menu.addItem(
        isValid
          ? 'Group Selected'
          : `Group Selected ⚠️ (${validation.ok ? 'Already grouped' : validation.reason === 'disconnected' ? 'Disconnected' : 'Has gaps'})`,
        isValid ? null : '/icon/warning.svg',
        () => {
          if (isLocked) {
            toast?.showWarning(getEditBlockedMessage(blockedState));
            return;
          }
          if (!isValid) {
            const missingStepsFormatted = (!validation.ok ? (validation.missingStepIds || []) : [])
              .map((id: string) => formatStepForDisplay(id, store))
              .join(', ');
            toast?.showWarning(
              validation.ok
                ? 'Some steps already belong to another group.'
                : validation.reason === 'disconnected'
                  ? 'Selected steps are not connected.'
                  : `Selected steps have gaps. Missing: ${missingStepsFormatted || '?'}`,
            );
            return;
          }
          store.openModal({
            kind: 'add-group',
            payload: { stepIds: opIds },
          });
        },
      );
    }
  }
}
